import { get, indexOf } from "lodash";
import { BlockTreeBlock } from "modules/MainSurveyEditor/components/SubSections/BlockTreeBlock";
import React, { useCallback, useState } from "react";
import { mainSurveyClient } from "../../client/mainSurveyClient";
import { Blockable, BlockNestedKey, getParentPath, setPathNewIndex } from "../../helpers/blockTree";
import { BlockTypes, getBlockFullType, getSplitBlockType } from "../../helpers/blocks";
import { useCopyAndPasteBlocks } from "../../hooks/useCopyAndPasteBlocks";
import { MainSurveyStore } from "../../hooks/useMainSurvey";
import { BlockRequiredDialog } from "../SubSections/BlockRequiredDialog";
import { PasteBlocksDialog } from "../SubSections/PasteBlocksDialog";
import { CopyPasteBlockActions } from "./CopyPasteBlockActions";

export const BlockTree = (props: BlockTreeProps) => {
  const {
    mainSurveyStore: {
      mainSurveyTree,
      setMainSurveyTree,
      draggedBlockPath,
      setDraggedBlockPath,
      setDraggedBlockPosition,
      newBlockTypeToBeAdded,
      blockToEdit,
      setBlockToEdit,
      setBlockToAdd,
      setNewBlockTypeToBeAdded,
      handleAddBlock,
      handleAddExistingBlock,
      handleUpdateTreeAfterBlockChange,
      handleOnExpand,
      existingBlockToAdd,
      blockToAdd,
      selectedBlocks,
      setSelectedBlocks,
      silentRefetchSurvey,
    },
  } = props;

  const copyAndPasteBlocksStore = useCopyAndPasteBlocks();
  const { setCopiedBlocks, startPaste, copiedBlocks } = copyAndPasteBlocksStore;

  const [dragOverParentPath, setDragOverParentPath] = useState<string | null>(null);

  const handleDragStart = useCallback(
    (e: React.DragEvent, blockPath: string, blockPosition: string | null = null) => {
      setDraggedBlockPath(blockPath);
      setDraggedBlockPosition(blockPosition);
    },
    [setDraggedBlockPath, setDraggedBlockPosition]
  );

  const handleDragOverEndOfTree = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      if (dragOverParentPath !== "") {
        setDragOverParentPath("");
      }
    },
    [dragOverParentPath]
  );

  const handleDrop = useCallback(
    async (e: React.DragEvent, parentPath: string, toIndex: number) => {
      const temp = { ...mainSurveyTree };
      let block: Blockable | undefined;
      const parent = (get(temp, parentPath) || temp) as Blockable;
      if (newBlockTypeToBeAdded) {
        const blockTypes = getSplitBlockType(newBlockTypeToBeAdded);
        setBlockToAdd({
          parentSurveyId: parent.objectId,
          parentPath,
          type: blockTypes.blockType,
          subType: blockTypes.blockSubType,
          toIndex,
        });
        setNewBlockTypeToBeAdded(null);

        return;
      }

      if (existingBlockToAdd) {
        handleAddExistingBlock({ parent, parentId: parent.objectId, toIndex, parentPath });

        return;
      }

      if (!draggedBlockPath) return;

      block = get(temp, draggedBlockPath!) as Blockable;
      if (!block) return;

      const currentParentPath = getParentPath(draggedBlockPath);
      const currentParent = (get(temp, currentParentPath) || temp) as Blockable;
      const indexOfBlock = indexOf(currentParent[BlockNestedKey], block);
      const calcIndex = currentParent.objectId === parent.objectId && toIndex > indexOfBlock ? toIndex - 1 : toIndex;
      await mainSurveyClient.updateSurveyBlock(currentParent.objectId, block.id, {
        surveyId: parent.objectId,
        order: calcIndex,
      });

      const indexOfParentInsideCurrentParent = indexOf(currentParent[BlockNestedKey], parent);
      if (indexOfParentInsideCurrentParent > indexOfBlock) {
        parentPath = setPathNewIndex(parentPath, indexOfParentInsideCurrentParent - 1);
      }
      currentParent[BlockNestedKey] = (currentParent[BlockNestedKey] ?? []).filter((b) => b.id !== block!.id);
      
      handleUpdateTreeAfterBlockChange(temp, block!, parent, parentPath, calcIndex);
    },
    [
      mainSurveyTree,
      newBlockTypeToBeAdded,
      existingBlockToAdd,
      draggedBlockPath,
      handleUpdateTreeAfterBlockChange,
      setBlockToAdd,
      setNewBlockTypeToBeAdded,
      handleAddExistingBlock,
    ]
  );

  const handleDropEndOfTree = useCallback(
    (e: React.DragEvent) => {
      setDragOverParentPath(null);
      handleDrop(e, "", mainSurveyTree[BlockNestedKey]?.length || 0);
    },
    [handleDrop, mainSurveyTree]
  );

  const handleOnClick = useCallback(
    (e: React.MouseEvent, blockPath: string) => {
      const block = get(mainSurveyTree, blockPath) as Blockable;
      const parentPath = getParentPath(blockPath);
      const parent = (get(mainSurveyTree, parentPath) || mainSurveyTree) as Blockable;
      if (e.metaKey) {
        setSelectedBlocks((prev) => {
          if (prev.find((b) => b.id === block.id)) {
            return prev.filter((b) => b.id !== block.id);
          }

          return [...prev, block];
        });

        return;
      }
      if (e.shiftKey) {
        setSelectedBlocks((prev) => {
          const start = (parent[BlockNestedKey] || []).findIndex((b) => b.id === prev[prev.length - 1]?.id);
          const end = (parent[BlockNestedKey] || []).findIndex((b) => b.id === block.id);
          if (end === -1 || start === -1) {
            return prev;
          }

          const selected = parent[BlockNestedKey]!.slice(Math.min(start, end), Math.max(start, end) + 1) || [];
          const newSelected = selected.filter((b) => !prev.find((pb) => pb.id === b.id));
          return [...prev, ...newSelected];
        });

        return;
      }

      setBlockToEdit({
        id: block?.id,
        type: block?.blockType || BlockTypes.MainSurvey,
        parentSurveyId: parent.objectId,
        blockPath: blockPath,
      });

      setSelectedBlocks(block ? [block] : []);
    },
    [mainSurveyTree, setBlockToEdit, setSelectedBlocks]
  );

  const handlePasteClick = useCallback(() => {
    let toIndex = 0;
    let parentSurveyId = blockToEdit.parentSurveyId;
    let parentPath = "";
    if (blockToEdit.type === BlockTypes.Survey) {
      parentSurveyId = get(mainSurveyTree, blockToEdit.blockPath!).objectId;
      parentPath = blockToEdit.blockPath!;
    } else if (blockToEdit.blockPath) {
      const blockIndexPath = blockToEdit.blockPath.match(/\[(\d+)\]$/);
      const blockIndex = blockIndexPath ? blockIndexPath[1] : null;
      toIndex = blockIndex ? parseInt(blockIndex) + 1 : 0;
      parentPath = getParentPath(blockToEdit.blockPath);
    }

    startPaste(toIndex, parentSurveyId, undefined, undefined, ({ createdBlocks }) => {
      if (!createdBlocks?.length) {
        silentRefetchSurvey();
        return;
      }

      const temp = { ...mainSurveyTree };
      const parent = (get(temp, parentPath) || temp) as Blockable;

      // Fall back to a silent server refetch if the parent's children aren't loaded yet.
      if (!parent[BlockNestedKey]) {
        silentRefetchSurvey();
        return;
      }

      const newBlockables: Blockable[] = createdBlocks.map((created) => ({
        id: created.newId!,
        objectId: created.newObjectId!,
        parentSurveyId,
        title: created.blockData.contentObject.title ?? "",
        blockType: getBlockFullType(created.blockData.contentType, created.blockData.contentObject.type),
      }));

      newBlockables.forEach((b, i) => {
        parent[BlockNestedKey]!.splice(toIndex + i, 0, b);
      });

      setMainSurveyTree(temp);
      setSelectedBlocks(newBlockables);
    });
  }, [blockToEdit, mainSurveyTree, startPaste, silentRefetchSurvey, setMainSurveyTree, setSelectedBlocks]);

  return (
    <div className="layout-block-tree">
      <CopyPasteBlockActions
        copiedBlocksCount={copiedBlocks?.length ?? 0}
        canCopy={
          !!selectedBlocks.length &&
          selectedBlocks.every((block) => ![BlockTypes.Survey, BlockTypes.MainSurvey].includes(block.blockType))
        }
        onPaste={handlePasteClick}
        onClear={() => setCopiedBlocks([])}
        onCopy={() => setCopiedBlocks(selectedBlocks)}
      />

      <div className="block-tree-section">
        <BlockTreeBlock
          index={0}
          tree={mainSurveyTree}
          treePath={""}
          onDrop={handleDrop}
          onDragStart={handleDragStart}
          setDragOverParentPath={setDragOverParentPath}
          dragOverParentPath={dragOverParentPath}
          handleOnClick={handleOnClick}
          isNotDraggable
          blockToEdit={blockToEdit}
          handleOnExpand={handleOnExpand}
          draggedBlockPath={draggedBlockPath}
          selectedBlocks={selectedBlocks}
        />
        <div className="module-add-new" onDrop={handleDropEndOfTree} onDragOver={handleDragOverEndOfTree} />
      </div>

      <BlockRequiredDialog
        blockToAdd={blockToAdd}
        onCancel={() => setBlockToAdd(null)}
        onSuccess={handleAddBlock}
        inNotFieldId={[BlockTypes.Survey, BlockTypes.RichText].includes(blockToAdd?.type as BlockTypes)}
      />

      <PasteBlocksDialog copyAndPasteBlocksStore={copyAndPasteBlocksStore} />
    </div>
  );
};

interface BlockTreeProps {
  mainSurveyStore: MainSurveyStore;
}
